import { create } from 'zustand';

import { closeOtherRightSlotSurfaces, registerRightSlotCloser } from './_right-slot-mutex';

import type { BoardComposerHandoff } from '@/lib/content-engine/board-composer-handoff';
import type { DiscoverAdProvider } from '@/lib/discover/context-types';

export type RightPanelType =
  'engagement' | 'drafts' | 'agent' | 'idea' | 'ad' | 'sources' | 'reach' | null;

/**
 * What the `agent` panel opens BOUND to.
 *
 * The competitor's `Run ›` does not navigate: the page stays and a right panel
 * opens already holding a sent message and a named responder. Both halves of
 * that pair have to arrive together or the panel opens onto a blank composer,
 * so they travel as one seed value rather than as panel state the caller sets
 * afterwards.
 */
export interface AgentPanelLaunch {
  /**
   * One id for one run. Doubles as the chat session id (`ai_sessions.id`, which
   * the agent route upserts) and as the panel's remount key, so running the
   * SAME agent twice starts a genuinely new thread instead of appending to the
   * last one. Minted by `openAgentPanel`, never by callers.
   */
  runId: string;
  /**
   * Durable chat identity. Generic panel launches intentionally keep their
   * existing run-as-session behavior. A Brief-bound launch leaves this absent
   * until the actor-private bootstrap RPC has identified (or minted) a
   * session; `runId` must never be inferred as a Brief command session.
   */
  sessionId?: string;
  /**
   * Stable identity of the ENTRY that opened this launch (agent + prompt + slash
   * binding + context ids). Minted by `resolveAgentLaunch`, never by callers.
   * Pressing the same entry again after a collapse restores the thread instead
   * of starting a new run — see `agentLaunchIdentity.ts` for the rule.
   */
  entryKey?: string;
  /**
   * True when `sessionId` names a conversation that already exists server-side
   * (a restored collapse, or an earlier chat picked from the history popover).
   * The panel hydrates its history before the chat engine initializes, because
   * `useChat` reads initial messages exactly once.
   */
  resume?: boolean;
  /**
   * The competitor ad this run was launched FROM (`/discover/ads`). Forwarded
   * by `PanelAgentChat` into `pageContext.referenceAd` so `generate_ad_copy`
   * can persist provenance without depending on the model echoing an id back
   * out of the seeded prompt — it measured 0 of 33 rows before this existed.
   */
  referenceAd?: {
    provider: DiscoverAdProvider;
    adId: string;
    pageId?: string;
    advertiser?: string;
    primaryText?: string;
    headline?: string;
    description?: string;
  };
  /** Stable id of the agent being run. */
  agentId: string;
  /** Human name shown in the panel header and the footer attribution row. */
  agentName: string;
  /** Three-word job statement under the name. Omitted when we have none. */
  agentTagline?: string;
  /**
   * `agents.icon_id`, so the panel header wears the SAME mark the card the user
   * just clicked wears. Without it the panel opened under a bare name and the
   * visual thread from "I picked Brand researcher" to "this panel is Brand
   * researcher" was broken at exactly the moment it mattered.
   */
  agentIconId?: string | null;
  /** Pre-written first user message. See `seedMode` for whether it is sent. */
  prompt: string;
  /**
   * What the panel does with `prompt` on open.
   *
   *  `send`    — default, and what `Run ›` on an agent card means: the user
   *              read the card and asked for that named agent to run, so the
   *              seed goes out immediately.
   *  `prefill` — the seed lands in the composer with the cursor in it and
   *              spends nothing until the reader presses send. This is the
   *              contract every ANALYTICS affordance is under: "Ask about
   *              this" sits on a dashboard the reader is mid-scan of, and a
   *              brushed link that silently spends tokens is the resubmit-storm
   *              precedent. Those links used to satisfy that by navigating to
   *              the home composer via `buildAgentUrl`, which threw away the
   *              chart the question was about — this mode is what lets them
   *              keep the wait-for-me promise inside the panel instead.
   *  `none`    — the panel opens with nothing to send and nothing to prefill:
   *              "New chat" and a conversation loaded from the history popover.
   */
  seedMode?: 'send' | 'prefill' | 'none';
  /**
   * Slash key from `SLASH_REGISTRY` whose entry carries the skill to bind.
   * Transmitted on the request body, which is the deterministic server-side
   * path (`server/api/agent/index.post.ts` → `resolveSlashPrepareStep`), so
   * the seed message itself stays natural language.
   */
  slashCommand?: string;
  slashArgs?: string;
  /**
   * Present only when this run is a workflow_template agent. Binds the panel
   * to the project the launch created so `PanelAgentChat` can load workflow
   * progress and thread `pageContext.campaignId` for server-side binding.
   * `prompt` above IS the workflow's kickoff message — there is no separate
   * autostart flag; the existing send-once-per-`runId` mechanism fires it.
   */
  workflow?: {
    planId: string;
    templateId: string;
  };
  /**
   * Present when the panel was opened from a specific planned Brief concept.
   * These IDs are UI-originated context, never model output; the server-side
   * ad-copy tool still verifies their parent/owner relationship before use.
   */
  brief?: {
    briefId: string;
    projectTaskId?: string;
  };
  /**
   * Present when the panel was opened bound to a Reach event ("Plan this
   * event" in the event dock). UI-originated context only, same discipline as
   * `brief` above — the server re-reads and validates the event (owner and
   * workspace scope, dates, facts) before use; the model never sets this id.
   * See `docs/planning/mora-reach/EVENT-CONTENT-ORCHESTRATION-PLAN-2026-08-31.md`
   * §C.
   */
  reachEvent?: {
    eventId: string;
  };
  /**
   * The editorial topic this run was launched FROM (`Write` on a row of
   * `/reach/editorial`). Forwarded by `PanelAgentChat` into
   * `pageContext.editorialTopic` so the finished article can file itself back
   * against the topic without the model echoing a uuid out of the seeded
   * prompt — the same failure `referenceAd` above was built to close, which
   * measured 0 of 33 rows.
   *
   * See `context-types.ts`'s note on the field for the measurement that makes
   * this a join rather than a nicety.
   */
  /** Pillar selected for article planning. Server must revalidate workspace access and taxonomy. */
  editorialPillar?: {
    workspaceId: string;
    pillarId: string;
    branchId?: string;
  };
  editorialTopic?: {
    topicId: string;
    projectId: string;
    pillarId?: string | null;
    branchId?: string | null;
  };
}

/**
 * Which tab the engagement panel should open on.
 *
 * The panel is a dock surface, not a route, so it has no URL and nothing can
 * deep-link into it with a filter applied. This is the seam that lets a caller
 * say "open the inbox ON the messages I just told the user about" instead of
 * dropping them on the full list to search. `EngagementHub` reads it once at
 * mount (the panel remounts on every open) and owns its tab from then on.
 */
export type EngagementTabSeed = string | null;

/**
 * What the `idea` panel opens ON.
 *
 * Unlike every other dock panel this carries a SNAPSHOT rather than an id to
 * look up, and that is a deliberate exception to the "panels read their context
 * from the URL" rule. Most board ideas are generated per-render and have no
 * durable row at all — only a PINNED idea has a `board_ideas.id`. A panel keyed
 * on an id could therefore open on roughly a third of the cards on the surface
 * and would silently fail on the rest.
 *
 * `pinId` is still carried when it exists, and the panel prefers live pinned
 * data over the snapshot whenever it can — so editing a note in the panel
 * writes through to the row rather than to a stale copy.
 */
export interface IdeaPanelLaunch {
  /** Stable key for the idea within the render (`BoardIdea.id`). */
  ideaKey: string;
  /** `board_ideas.id` when this idea is pinned; null when it is ephemeral. */
  pinId: string | null;
  title: string;
  angle?: string | null;
  context?: string | null;
  imageUrl?: string | null;
  sourceLabel?: string | null;
  sourceDetail?: string | null;
  sourceHref?: string | null;
  observedAt?: string | null;
  /** True when the idea is grounded in a real row, not a hardcoded prompt. */
  isEvidence: boolean;
  /**
   * The composer hand-off, PRE-BUILT by the board at click time.
   *
   * `buildBoardIdeaHandoff` needs the whole `BoardIdea` plus live account
   * context (the brand-kit name, whether a Shopify catalog exists) that the
   * globally-mounted panel has no business re-deriving. It is plain data, so
   * it travels here and the panel just fires it — which also guarantees the
   * panel's "Write it now" and the card's old click produce the byte-identical
   * hand-off, including the `referenceRole` that keeps a competitor's photo
   * from reaching the image model as a copyable asset.
   */
  write: BoardComposerHandoff;
}

/** One Meta ad instance inside a creative the panel is showing. */
export interface AdPanelInstance {
  adId: string;
  primaryText: string | null;
  headline: string | null;
  description: string | null;
  platforms: string[];
  deliveryStartTime: string | null;
  deliveryStopTime: string | null;
  snapshotUrl: string;
}

/**
 * What the `ad` panel opens ON.
 *
 * WHY A DEDICATED PANEL TYPE RATHER THAN REUSING `idea`. A competitor ad opened
 * through the generic idea panel and, because `pinId` was null on a browsed ad,
 * `ExternalAdDetail` — the component that actually knows how to render an ad —
 * never rendered until AFTER the user saved. The person deciding whether to save
 * saw strictly less than the person who already had. Every product in
 * `2026-08-17-32-ad-detail-view-catalogue-and-gap-analysis.md` shows full
 * metadata on first click; that inversion is the thing this type exists to end.
 *
 * Like `IdeaPanelLaunch` it carries a SNAPSHOT, not an id, and for the same
 * reason: a browsed ad has no durable row anywhere until it is saved, so a panel
 * keyed on an id could not open at all.
 *
 * Everything here is either a field Meta returned or a value Mora derived and
 * labels as its own. Nothing is inferred (`GOAL-AD-LIBRARY` §5, criteria 1-4).
 */
export interface AdPanelLaunch {
  /** Remount key. `meta:{adId}` of the group's representative. */
  adKey: string;
  /** Discover context captured when the ad was opened. */
  ownerId?: string;
  brandKitId?: string;
  contextRevision?: number;
  /**
   * The `board_ideas` row this ad is already saved as, when the panel was
   * opened from a saved reference rather than a live search result.
   *
   * Present ⇒ the ad has a durable row, which is the only thing an evidence
   * attach can address. Absent ⇒ the ad is being browsed and must be saved
   * before it can become evidence; the panel does that save itself rather than
   * telling the reader to go and do it.
   */
  boardIdeaId?: string;
  /** Source provider; distinct from the Meta/LinkedIn placements in `platforms`. */
  provider: DiscoverAdProvider;
  advertiserName: string;
  advertiserPageId: string | null;
  /** Mora-hosted copy of the advertiser's mark. Provenance, never creative. */
  advertiserLogoUrl: string | null;
  /** `null` = creative still hydrating. `[]` = a settled "none obtainable". */
  creative: Array<{ kind: 'image' | 'video'; url: string }> | null;
  ctaLabel: string | null;
  landingUrl: string | null;
  /** Earliest delivery start Meta reported across the group. */
  firstSeen: string | null;
  anyActive: boolean;
  /** When MORA looked. Deliberately distinct from `firstSeen` (criterion 4). */
  observedAt: string;
  platforms: string[];
  /**
   * Every Meta ad instance MORA grouped into this one creative. Meta exposes no
   * creative identity, so any count drawn from this must be attributed to Mora,
   * never presented as something Meta reported.
   */
  instances: AdPanelInstance[];
  /** The public Ad Library URL for the representative ad. */
  snapshotUrl: string;
  /** Pre-built composer hand-off, same contract as `IdeaPanelLaunch.write`. */
  write: BoardComposerHandoff;
}

/**
 * What the `sources` panel opens holding.
 *
 * Carried as a seed, like `agentLaunch`, rather than re-derived in the panel:
 * the sources belong to ONE assistant turn, and the panel has no way to know
 * which turn was clicked. `messageId` keys the remount so opening a second
 * turn's sources replaces the list instead of showing the first turn's.
 *
 * The row shape is duplicated here rather than importing `MessageSource` from
 * `components/create/v2/utils/collect-message-sources.ts` — this store is a
 * shared layer other surfaces (the style guide, any future non-chat launcher)
 * can seed without pulling in a chat-feature module. Keep it in sync by hand;
 * `kind: 'document'` sources have no `domain` and may have no `url` — see
 * that file's `MessageSource` doc comment for why.
 */
export interface SourcesPanelLaunch {
  messageId: string;
  sources: ReadonlyArray<{
    id: string;
    kind: 'web' | 'document';
    title: string;
    domain?: string;
    url?: string;
    documentId?: string;
    excerpt?: string;
  }>;
}

/**
 * What the `reach` panel opens ON (PRD §16.1). `eventId` is optional — a
 * launch from the People pool (no event in view) omits it, and the dock's
 * event-scoped "FOR [event]" section simply does not render; a launch from an
 * event's People tab carries it so that section can show priority/why-now.
 *
 * `entityType: 'outreach'` (Round 2B, C3) is the outreach draft editor —
 * `entityId` is a `reach_outreach_actions.id`. Unlike person/company it
 * carries no snapshot: the panel body re-reads the live row off the same
 * `useReachOutreachAll` cache `/reach/outreach` already populates, so a
 * mutation from the page (or from the panel itself) is visible in both
 * places without a second, independently-updated copy of the action.
 */
/**
 * `entityType: 'event'` (2026-08-25) replaced a routed five-tab workspace at
 * `/reach/events/:eventId`. An event's own facts — dates, location, booth, URL,
 * status — are a property rail you dip into, which is design rule 24's
 * definition of a panel; the WORK happens on the people it produced. The tabs
 * that page carried were re-mounting `/reach/people` and `/reach/outreach`
 * inside itself, so the product had been built twice.
 */
export interface ReachPanelLaunch {
  entityType: 'person' | 'company' | 'outreach' | 'event' | 'approval' | 'editorial-topic';
  entityId: string;
  eventId?: string;
  /**
   * `editorial-topic` only: the short ref of one critique finding to scroll to and highlight
   * (`editorialFindingRef`), set by the Slack note deep link on `/reach/editorial`.
   */
  findingRef?: string;
  /** `person` only: open the dock with the "Log a touch" form already showing. */
  openLog?: boolean;
}

interface RightDockState {
  activePanel: RightPanelType;
  sourcesLaunch: SourcesPanelLaunch | null;
  engagementTab: EngagementTabSeed;
  agentLaunch: AgentPanelLaunch | null;
  /**
   * The agent launch the user COLLAPSED (header "Collapse panel"). Held so
   * pressing the same entry again restores the thread; `resolveAgentLaunch`
   * decides whether a new request matches it. Cleared by any real close and by
   * opening an agent panel (restored or new).
   */
  minimizedAgentLaunch: AgentPanelLaunch | null;
  ideaLaunch: IdeaPanelLaunch | null;
  adLaunch: AdPanelLaunch | null;
  reachLaunch: ReachPanelLaunch | null;
  /**
   * True while a full-viewport workspace (the Brief shell) hosts the agent chat
   * itself. The global dock yields the `agent` panel then, so exactly one chat
   * mounts. Deliberately untouched by `closePanel`: it follows the host's mount.
   */
  agentHostedInline: boolean;
  setAgentHostedInline: (hosted: boolean) => void;
  openPanel: (
    panel: RightPanelType,
    options?: {
      engagementTab?: string;
      agentLaunch?: AgentPanelLaunch;
      ideaLaunch?: IdeaPanelLaunch;
      adLaunch?: AdPanelLaunch;
      sourcesLaunch?: SourcesPanelLaunch;
      reachLaunch?: ReachPanelLaunch;
    },
  ) => void;
  closePanel: () => void;
  /** Close the agent panel but keep its launch so the same entry can restore it. */
  minimizeAgentPanel: () => void;
  togglePanel: (panel: RightPanelType) => void;
}

export function closeCreateScopedRightSlotSurfaces(): void {
  closeOtherRightSlotSurfaces('global-dock');
}

export const useRightDockStore = create<RightDockState>((set, get) => ({
  activePanel: null,
  engagementTab: null,
  agentLaunch: null,
  minimizedAgentLaunch: null,
  ideaLaunch: null,
  adLaunch: null,
  sourcesLaunch: null,
  reachLaunch: null,
  agentHostedInline: false,
  setAgentHostedInline: (hosted) => set({ agentHostedInline: hosted }),
  openPanel: (panel, options) => {
    if (panel) closeOtherRightSlotSurfaces('global-dock');
    // Cleared on every open, so a seeded open never leaks into the next plain
    // one — the sidebar's own "Engagement" click must always land on the
    // default tab, whatever the last deep link asked for. Same rule for the
    // agent seed: a second Run must never inherit the first Run's prompt.
    set({
      activePanel: panel,
      engagementTab: options?.engagementTab ?? null,
      agentLaunch: panel === 'agent' ? (options?.agentLaunch ?? null) : null,
      minimizedAgentLaunch: panel === 'agent' ? null : get().minimizedAgentLaunch,
      ideaLaunch: panel === 'idea' ? (options?.ideaLaunch ?? null) : null,
      adLaunch: panel === 'ad' ? (options?.adLaunch ?? null) : null,
      sourcesLaunch: panel === 'sources' ? (options?.sourcesLaunch ?? null) : null,
      reachLaunch: panel === 'reach' ? (options?.reachLaunch ?? null) : null,
    });
  },
  closePanel: () => {
    set({
      activePanel: null,
      engagementTab: null,
      agentLaunch: null,
      // The mutex also calls this when ANOTHER surface opens while the dock is
      // already closed; that must not discard a collapsed thread.
      minimizedAgentLaunch: get().activePanel ? null : get().minimizedAgentLaunch,
      ideaLaunch: null,
      adLaunch: null,
      sourcesLaunch: null,
      reachLaunch: null,
    });
  },
  minimizeAgentPanel: () => {
    const { activePanel, agentLaunch } = get();
    if (activePanel !== 'agent' || !agentLaunch) return;
    set({
      activePanel: null,
      engagementTab: null,
      agentLaunch: null,
      minimizedAgentLaunch: agentLaunch,
      ideaLaunch: null,
      adLaunch: null,
      sourcesLaunch: null,
      reachLaunch: null,
    });
  },
  togglePanel: (panel) => {
    const nextPanel = get().activePanel === panel ? null : panel;
    if (nextPanel) closeOtherRightSlotSurfaces('global-dock');
    set({
      activePanel: nextPanel,
      engagementTab: null,
      agentLaunch: null,
      ideaLaunch: null,
      adLaunch: null,
      sourcesLaunch: null,
      reachLaunch: null,
    });
  },
}));

registerRightSlotCloser('global-dock', () => useRightDockStore.getState().closePanel());

// @ts-expect-error — __useRightDockStore is a non-standard debug property not in WindowEventHandlersEventMap
if (typeof window !== 'undefined') window.__useRightDockStore = useRightDockStore;
